import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import Tour from "reactour";
import { setHelpDrawerOpen } from "src/redux/reducers/HelpDrawer";
import { useTour } from "../../../Docuclipper/Tour/TourContext";
import {
  SUB_TOUR_CATEGORIZE,
  SUB_TOUR_FRAUD
} from "../../../Docuclipper/Tour/tourDefinitions";
import { TourDefinition, TourStep } from "../../../Docuclipper/Tour/types";

const steps = [
  {
    selector: ".transation-manager-select-document-id",
    content:
      "Select a document. DocuClipper displays the transactions and summary information of one document at a time.",
  },
  {
    selector: ".tx-manager-account-selector",
    content:
      "Select the account e.g. 'checking'. A blue badge indicates the account is reconciled.",
  },
  {
    selector: ".tx-manager-period-selector",
    content:
      "Select the period, e.g. '2023-01-01 to 2023-01-31'. A blue badge indicates the period is reconciled.",
  },
  {
    selector: ".summary-table-dates-balances",
    content:
      "Summary information including totals, number of credits and debits, etc. Edit start/end dates and start/end balances for a given period.",
  },
  {
    selector: ".transaction-manager",
    content:
      "Browse the transactions. Click on the filters to select only certain transactions.",
  },
  {
    selector: ".ag-grid-cell-included",
    content: "Include or exclude each transaction using the toggle.",
  },
  {
    selector: ".ag-grid-cell-date",
    content:
      "Correct the values of the transactions by clicking on the cell and entering a new value.",
  },
  {
    selector: ".add-transaction-btn",
    content: 'Click on "Add Transaction" to add any missing transactions.',
  },

  {
    selector: ".ag-grid-cell-selection",
    content: "Select one or more transactions",
  },
  {
    selector: ".bulk-edit-buttons",
    content: "Make changes to the selected transations in bulk.",
  },
  {
    selector: ".assign-to-account-btn",
    content: "Assign selected transactions to a different account or period.",
  },
  {
    selector: ".btn--report-bad-job",
    content: "Get help and report issues to the support team",
  },

  {
    selector: ".btn--download-data",
    content:
      "Download your data. DocuClipper supported formats include Excel, QuickBooks, Xero and other third party applications.",
  },
];

/**
 * Build a contextual sub-tour by re-pointing every step at the URL the user
 * is currently on. The shared sub-tour definitions hard-code the sandbox's
 * Default Folder / Default Project route; without re-pointing, starting one
 * in a regular account would `history.push` the user to that sandbox URL
 * and break their session. All the categorize/fraud sub-tour steps live on
 * one URL each, so it's safe to flatten every step's route to the current
 * one.
 */
function buildContextualTour(
  base: TourDefinition,
  currentRoute: string
): TourDefinition {
  const steps: TourStep[] = base.steps.map((s) => ({
    ...s,
    route: currentRoute,
    resolveRoute: undefined
  }));
  return { id: `${base.id}-tutorial`, steps };
}

export const TransactionManagerTourAndReportIssue: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { registerTour, startTour } = useTour();

  const [open, setOpen] = React.useState(false);
  const handleCloseTour = () => {
    localStorage.setItem("hasSeenTransactionManagerTour", "true");
    setOpen(false);
  };

  // Pick the right tour for the active sub-tab. Categorize and Fraud each
  // have a dedicated sub-tour with content + selectors that match the page;
  // the legacy reactour `steps` above are reconcile-specific. For modes that
  // don't have a new sub-tour yet (reconcile), we fall back to the legacy
  // reactour so the button still does something.
  const handleStartTour = () => {
    const sp = new URLSearchParams(location.search);
    const mode = sp.get("mode") || "reconcile";
    const route = `${location.pathname}${location.search}`;

    let base: TourDefinition | null = null;
    if (mode === "categorize") base = SUB_TOUR_CATEGORIZE;
    else if (mode === "fraud") base = SUB_TOUR_FRAUD;

    if (base) {
      const def = buildContextualTour(base, route);
      registerTour(def);
      startTour(def.id);
      return;
    }

    // Reconcile + anything else → keep the legacy tour
    setOpen(true);
  };

  return (
    <>
      <Button
        className="btn--report-bad-job mx-2 text-muted"
        variant="link"
        size="sm"
        onClick={() => {
          dispatch(setHelpDrawerOpen(true));
        }}
      >
        <FontAwesomeIcon icon="exclamation-triangle" className="mr-1" />
        Report Issue
      </Button>
      <Button
        onClick={handleStartTour}
        variant="link"
        size="sm"
        className="mx-2 text-muted"
      >
        <FontAwesomeIcon icon="play" className="mr-1" />
        Take a tour
      </Button>
      <Tour steps={steps} isOpen={open} onRequestClose={handleCloseTour} />
    </>
  );
};
export default TransactionManagerTourAndReportIssue;
